"use client";

import { useEffect, useRef, useState } from "react";
import { connectCentrifugo, disconnectCentrifugo } from "@/src/lib/connectCentrifugo";
import { ACCESS_TOKEN_KEY } from "@/api/base/private/axiosPrivateClient";

export function useCentrifugoAuth(): void {
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  });
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleAuthTokenChanged = () => {
      setAccessToken(localStorage.getItem(ACCESS_TOKEN_KEY));
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY) return;
      setAccessToken(e.newValue);
    };

    window.addEventListener("auth-token-changed", handleAuthTokenChanged);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("auth-token-changed", handleAuthTokenChanged);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!accessToken) {
      lastTokenRef.current = null;
      disconnectCentrifugo();
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("centrifugo-status-changed", {
            detail: { status: "disconnected" },
          }),
        );
      }
      return;
    }

    if (lastTokenRef.current === accessToken) return;

    lastTokenRef.current = accessToken;

    void connectCentrifugo(accessToken).catch((err) => {
      console.error("Failed to connect Centrifugo:", err);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("centrifugo-status-changed", {
            detail: {
              status: "error",
              message:
                err instanceof Error && err.message.trim()
                  ? err.message
                  : "Failed to connect Centrifugo",
            },
          }),
        );
      }
    });
  }, [accessToken]);
}
